import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  Tooltip,
  Stack,
  Container,
  Grid,
  Badge,
  Chip,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

import * as Actions from "actions/user/user-base.actions";
import ButtonComponent from "../../../Button";
import HeaderBack from "components/elements/main/HeaderBack";
import CustomSectionCreator from "components/CustomSectionCreator";
import ImageIcon from '@mui/icons-material/Image';

const Input = styled("input")({
  display: "none",
});

const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "inline-block",
  "& .MuiAvatar-root": {
    width: 200,
    height: 200,
    border: `4px solid ${theme.palette.primary.main}`,
    boxShadow: theme.shadows[4],
  },
}));

const VirtualTryOnProfileImageEditor = (props) => {
  const {
    serviceError,
    reqStatus,
    isLoading,
    tryonProfileImage,
    tryonProfileImages,
    uploadTryOnProfileImage,
    getTryOnProfileImage,
    getAllTryOnProfileImages,
    deleteTryOnProfileImage,
      history
  } = props;

  const [selectedFile, setSelectedFile] = React.useState(null);

  React.useEffect(() => {
    // Load all profile images on mount
    getAllTryOnProfileImages();
    getTryOnProfileImage(); // Keep this for backward compatibility
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert("Image size must be less than 10MB");
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file");
      return;
    }

    // Create FormData and upload
    const formData = new FormData();
    formData.append("user_image", file);
    
    uploadTryOnProfileImage(formData);
  };

  const handleDelete = (imageId) => () => {
    if (!window.confirm("Are you sure you want to delete this profile image?")) {
      return;
    }

    deleteTryOnProfileImage({ imageId });
  };

  const handleDeleteAll = () => {
    if (!window.confirm("Are you sure you want to delete ALL profile images?")) {
      return;
    }

    deleteTryOnProfileImage({});
  };

  const getValidationMessage = () => {
    if (tryonProfileImages && tryonProfileImages.length > 0) {
      return "Image should contain a clear, front-facing photo of a person";
    }
    return "";
  };

  const renderImageGallery = () => {
    if (!tryonProfileImages || tryonProfileImages.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          <Avatar
            variant="square"
            sx={{
              width: 300,
              height: 400,
              margin: "0 auto",
              bgcolor: "grey.200",
            }}
          >
            <UploadIcon sx={{ fontSize: 80, color: "grey.500" }} />
          </Avatar>
          <Typography variant="body2" color="text.secondary" mt={2}>
            No profile images uploaded yet
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container xs={12} sm={12} md={12} lg={12} spacing={2} justifyContent="center">
        {tryonProfileImages.map((image, index) => (
          <Grid item key={image._id || index} xs={6} sm={3} md={3} lg={3}>
            <Box position="relative">
              {index === 0 && (
                <Chip
                  icon={<StarIcon />}
                  label="Primary (Used for Try-On)"
                  color="primary"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    zIndex: 1,
                  }}
                />
              )}
              <Avatar
                variant="square"
                src={image.cdn?.secure_url}
                alt={`Profile ${index + 1}`}
                sx={{
                  width: "100%",
                  height: 300,
                  border: index === 0 ? "3px solid" : "2px solid",
                  borderColor: index === 0 ? "primary.main" : "grey.300",
                  boxShadow: 2,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  display: "flex",
                  gap: 1,
                }}
              >
                <Tooltip title="Delete this image">
                  <IconButton
                    onClick={handleDelete(image._id)}
                    color="error"
                    disabled={isLoading}
                    size="small"
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Container>
      <HeaderBack history={history} />
      <CustomSectionCreator isSubtle={true} icon={<ImageIcon />} title="Try-On Profiles" />
      <Card elevation={2}>
        <CardContent>
          <Stack spacing={3}>
            {/* Header */}
            <Box>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                Upload Profile Image for Try-Ons!
              </Typography>
            </Box>

            {/* Error Alert */}
            {serviceError && reqStatus && reqStatus.includes("FAILURE") && (
              <Alert severity="error">
                {serviceError.statusText || "An error occurred"}
              </Alert>
            )}

            {/* Success Alert */}
            {reqStatus === "UPLOAD_TRYON_PROFILE_IMAGE_SUCCESS" && (
              <Alert severity="success">
                Profile image uploaded successfully!
              </Alert>
            )}

            {reqStatus === "DELETE_TRYON_PROFILE_IMAGE_SUCCESS" && (
              <Alert severity="success">
                Profile image deleted successfully
              </Alert>
            )}

            {/* Image Gallery */}
            <Box py={2}>
              {renderImageGallery()}
            </Box>

            {/* Validation Message */}
            {getValidationMessage() && (
              <Alert severity="info" icon={<CheckIcon />}>
                {getValidationMessage()}
              </Alert>
            )}

            {/* Upload Button */}
            <Box display="flex" justifyContent="center" gap={2}>
              <label htmlFor="upload-profile-image">
                <Input
                  accept="image/*"
                  id="upload-profile-image"
                  type="file"
                  onChange={handleFileSelect}
                  disabled={isLoading}
                />
                <Button
                  color="primary"
                  variant="contained"
                  component="span"
                  startIcon={
                    isLoading ? <CircularProgress size={20} /> : <UploadIcon />
                  }
                  disabled={isLoading}
                  size="large"
                >
                  {isLoading ? "Uploading..." : tryonProfileImages?.length > 0 ? "Add Another Image" : "Upload Profile Image"}
                </Button>
              </label>
              {tryonProfileImages?.length > 0 && (
                <Button
                  color="error"
                  variant="outlined"
                  onClick={handleDeleteAll}
                  disabled={isLoading}
                  size="large"
                  startIcon={<DeleteIcon />}
                >
                  Delete All
                </Button>
              )}
            </Box>

            {/* Guidelines */}
            <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Image Guidelines:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
                <li>Full length photo Front-facing photo</li>
                <li>Good lighting on face - clearly visible</li>
                <li>Plain background preferred</li>
                <li>No sunglasses, cap or face coverings</li>
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

// Mapping Props and State
const virtualTryOnProfileImageEditor = connect(
  ({ userbootupInfo }) => ({
    reqStatus: userbootupInfo.reqStatus,
    isLoading: userbootupInfo.isLoading,
    serviceError: userbootupInfo.serviceError,
    tryonProfileImage: userbootupInfo.tryonProfileImage,
    tryonProfileImages: userbootupInfo.tryonProfileImages,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        uploadTryOnProfileImage: Actions.uploadTryOnProfileImage,
        getTryOnProfileImage: Actions.getTryOnProfileImage,
        getAllTryOnProfileImages: Actions.getAllTryOnProfileImages,
        deleteTryOnProfileImage: Actions.deleteTryOnProfileImage,
      },
      dispatch
    )
)(VirtualTryOnProfileImageEditor);

export { virtualTryOnProfileImageEditor as default };
